import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Select, Table, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faStop, faDownload, faUpload, faPlay } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import { EndpointMessageInterface } from "../../../interfaces/EndpointMessage.interface";
import { useEndpointMessages } from "./EndpointMessagesContext";
import TerminalEndpointColumns from "./TerminalEndpointColumns";
import TerminalEndpointDetail from "./TerminalEndpointDetail";

const { Option } = Select;

function filterByStatus(status: number, statusFilter: string): boolean {
  if (statusFilter === "All") return true;
  if (statusFilter === "2xx") return status >= 200 && status < 300;
  if (statusFilter === "3xx") return status >= 300 && status < 400;
  if (statusFilter === "4xx") return status >= 400 && status < 500;
  if (statusFilter === "5xx") return status >= 500;
  return true;
}

interface Props {
  scrollY?: number;
}

export default function TerminalEndpointTable({ scrollY = 400 }: Props) {
  const {
    messages,
    clearMessages,
    mode,
    captureReady,
    startCapture,
    stopCapture,
    downloadCapture,
    importReplay,
    exitReplay,
  } = useEndpointMessages();
  const { terminalAutoScroll } = useSelector((state: RootState) => state.monitoring);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [urlFilter, setUrlFilter] = useState<string>("");
  const [methodFilter, setMethodFilter] = useState<string>("All");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (terminalAutoScroll && tableBodyRef.current) {
      const body = tableBodyRef.current.querySelector(".ant-table-body");
      if (body) {
        body.scrollTop = body.scrollHeight;
      }
    }
  }, [messages.length, terminalAutoScroll]);

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      importReplay(file);
    }
    e.target.value = "";
  };

  const filteredMessages = messages.filter((msg) => {
    if (urlFilter && !msg.url.toLowerCase().includes(urlFilter.toLowerCase())) return false;
    if (methodFilter !== "All" && msg.method !== methodFilter) return false;
    if (!filterByStatus(msg.status, statusFilter)) return false;
    return true;
  });

  const columns = TerminalEndpointColumns();

  return (
    <div>
      <div className="generalStyles__flex" style={{ marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <Input
          placeholder="Filter by URL path"
          value={urlFilter}
          onChange={(e) => setUrlFilter(e.target.value)}
          style={{ width: 200 }}
          size="small"
          allowClear
        />
        <Select
          value={methodFilter}
          onChange={(val: string) => setMethodFilter(val)}
          style={{ width: 100 }}
          size="small"
        >
          <Option value="All">All</Option>
          <Option value="GET">GET</Option>
          <Option value="POST">POST</Option>
          <Option value="PUT">PUT</Option>
          <Option value="PATCH">PATCH</Option>
          <Option value="DELETE">DELETE</Option>
        </Select>
        <Select
          value={statusFilter}
          onChange={(val: string) => setStatusFilter(val)}
          style={{ width: 90 }}
          size="small"
        >
          <Option value="All">All</Option>
          <Option value="2xx">2xx</Option>
          <Option value="3xx">3xx</Option>
          <Option value="4xx">4xx</Option>
          <Option value="5xx">5xx</Option>
        </Select>
        <span style={{ fontSize: 11, color: "#888", alignSelf: "center" }}>
          {filteredMessages.length}/{messages.length}
        </span>
        {mode === "recording" && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#f5222d",
              display: "inline-block",
              alignSelf: "center",
              boxShadow: "0 0 0 2px rgba(245, 34, 29, 0.3)",
            }}
          />
        )}
        {mode === "replaying" && (
          <span style={{ fontSize: 11, color: "#1890ff", alignSelf: "center", fontWeight: 600 }}>
            REPLAY
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileImport}
            style={{ display: "none" }}
          />
          {mode !== "recording" ? (
            <Tooltip title="Record endpoint messages">
              <Button
                size="small"
                onClick={startCapture}
                disabled={mode === "replaying"}
                icon={<FontAwesomeIcon icon={faCircle} style={{ color: "#f5222d" }} />}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Stop recording">
              <Button
                size="small"
                onClick={stopCapture}
                danger
                icon={<FontAwesomeIcon icon={faStop} />}
              />
            </Tooltip>
          )}
          {captureReady && mode !== "recording" && (
            <Tooltip title="Download captured messages">
              <Button
                size="small"
                onClick={downloadCapture}
                icon={<FontAwesomeIcon icon={faDownload} />}
              />
            </Tooltip>
          )}
          {mode !== "recording" && (
            <Tooltip title="Import endpoint capture file">
              <Button
                size="small"
                onClick={() => fileInputRef.current?.click()}
                icon={<FontAwesomeIcon icon={faUpload} />}
              />
            </Tooltip>
          )}
          {mode === "replaying" && (
            <Tooltip title="Exit replay, return to live data">
              <Button
                size="small"
                type="primary"
                onClick={exitReplay}
                icon={<FontAwesomeIcon icon={faPlay} />}
              >
                Live
              </Button>
            </Tooltip>
          )}
          <Button size="small" danger onClick={clearMessages}>
            Clear All
          </Button>
        </div>
      </div>
      <div ref={tableBodyRef}>
        <Table<EndpointMessageInterface>
          dataSource={filteredMessages}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ y: scrollY }}
          expandable={{
            expandedRowKeys,
            onExpand: (expanded, record) => {
              setExpandedRowKeys(expanded ? [record.id] : []);
            },
            expandedRowRender: (record) => <TerminalEndpointDetail message={record} />,
            expandRowByClick: true,
          }}
          style={{ fontSize: 11 }}
        />
      </div>
    </div>
  );
}
